import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  User, GraduationCap, Languages, Briefcase, MapPin, ClipboardCheck,
  ArrowRight, ArrowLeft, Loader2, RotateCcw, Check, Target,
} from "lucide-react";
import { STEP_COMPONENTS } from "@/components/assessment/steps";
import { ReviewStep } from "@/components/assessment/ReviewStep";
import { useProfile } from "@/context/ProfileContext";
import { runAssessment, runStreamAssessment, fetchStreams } from "@/lib/api";
import { PROVINCE_COLORS, TEST_TYPES } from "@/lib/constants";
import { makeShow, visibleSteps } from "@/lib/streamMode";

const STEPS = [
  { id: "personal", label: "Personal", icon: User },
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "language", label: "Language", icon: Languages },
  { id: "work", label: "Work & Occupation", icon: Briefcase },
  { id: "connections", label: "Connections & Offer", icon: MapPin },
  { id: "review", label: "Review", icon: ClipboardCheck },
];

export default function Assessment() {
  const { profile, updateProfile, resetProfile, setResult, setStreamResult } = useProfile();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const streamId = params.get("stream");
  const [streamMeta, setStreamMeta] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setStep(0);
    if (!streamId) { setStreamMeta(null); return; }
    fetchStreams()
      .then((list) => {
        const found = list.find((s) => s.id === streamId);
        if (!found) { toast.error("Unknown stream"); navigate("/calculator"); }
        setStreamMeta(found || null);
      })
      .catch(() => toast.error("Could not load stream details"));
  }, [streamId, navigate]);

  const panelRef = useRef(null);
  useEffect(() => {
    if (step > 0 && panelRef.current && window.innerWidth < 1024) panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const show = useMemo(() => makeShow(streamMeta?.inputs), [streamMeta]);
  const steps = useMemo(() => visibleSteps(STEPS, show), [show]);
  const cur = steps[Math.min(step, steps.length - 1)].id;
  const spouseOn = profile.maritalStatus === "married" && profile.spouseAccompanying && show("spouse");

  const set = (patch) => updateProfile(patch);
  const setLang = (ability, value) =>
    updateProfile({ language: { ...profile.language, [ability]: value } });
  const setFrench = (ability, value) =>
    updateProfile({ french: { ...profile.french, [ability]: value } });
  const setSpouse = (patch) => updateProfile({ spouse: { ...profile.spouse, ...patch } });

  const setConnectionType = (code, type) => {
    const cur = [].concat(profile.connectionTypes?.[code] || []);
    const next = cur.includes(type) ? cur.filter((t) => t !== type) : [...cur, type];
    updateProfile({ connectionTypes: { ...(profile.connectionTypes || {}), [code]: next } });
  };
  const toggleConnection = (code) => {
    const cur = profile.provincialConnection || [];
    updateProfile({
      provincialConnection: cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code],
    });
  };

  const progress = ((step + 1) / steps.length) * 100;
  const test = TEST_TYPES.find((t) => t.value === profile.testType) || TEST_TYPES[0];
  const langRange = { min: test.min, max: test.max, stepv: test.step, ph: test.ph };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...profile,
        age: Number(profile.age) || 0,
        foreignExp: Number(profile.foreignExp) || 0,
        canadianExp: Number(profile.canadianExp) || 0,
        funds: Number(profile.funds) || 0,
        frenchCLB: Number(profile.frenchCLB) || 0,
        frenchTest: profile.frenchTest || "clb",
        french: Object.fromEntries(Object.entries(profile.french || {}).map(([k, v]) => [k, Number(v) || 0])),
        noc_teer: Number(profile.noc_teer),
        jobOfferTeer: Number(profile.jobOfferTeer),
        jobOfferWage: Number(profile.jobOfferWage) || 0,
        jobOfferMonths: Number(profile.jobOfferMonths) || 0,
        canadianEarnings: Number(profile.canadianEarnings) || 0,
        legalStatus: profile.legalStatus || "none",
        jobOfferRegion: profile.jobOfferRegion || "",
        nocCode: profile.nocCode || "",
        eduProvince: profile.eduInCanada ? (profile.eduProvince || "") : "",
        connectionTypes: Object.fromEntries((profile.provincialConnection || []).map((c) => {
          const t = [].concat(profile.connectionTypes?.[c] || []);
          return [c, t.length ? t : ["other"]];
        })),
        spouse: {
          ...profile.spouse,
          clb: Number(profile.spouse?.clb) || 0,
          canadianExp: Number(profile.spouse?.canadianExp) || 0,
          testType: profile.spouse?.testType || "clb",
          language: Object.fromEntries(Object.entries(profile.spouse?.language || {}).map(([k, v]) => [k, Number(v) || 0])),
        },
      };
      if (streamId) {
        const data = await runStreamAssessment(streamId, payload);
        setStreamResult(data);
        toast.success("Score calculated");
        navigate("/stream-result");
      } else {
        const data = await runAssessment(payload);
        setResult({ ...data, saved_at: new Date().toISOString() });
        toast.success("Assessment complete");
        navigate("/results");
      }
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const next = () => (step < steps.length - 1 ? setStep((s) => s + 1) : submit());
  const back = () => setStep((s) => Math.max(0, s - 1));

  if (streamId && !streamMeta) {
    return <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10 lg:px-8">
      <div className="mb-6 sm:mb-8">
        {streamMeta ? (
          <>
            <div className="flex flex-wrap items-center gap-2" data-testid="stream-mode-banner">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FEF2F2] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#C8102E]">
                <Target className="h-3.5 w-3.5" /> Stream score calculator
              </span>
              <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold text-white"
                style={{ backgroundColor: PROVINCE_COLORS[streamMeta.province] }}>{streamMeta.province}</span>
              <Link to="/calculator" data-testid="change-stream-link" className="text-xs font-semibold text-slate-500 underline hover:text-slate-900">Change stream</Link>
            </div>
            <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-testid="stream-mode-title">
              {streamMeta.name}
            </h1>
            <p className="mt-2 text-slate-600">
              {streamMeta.stream_group} · Only the {steps.length - 1} question group{steps.length - 1 === 1 ? "" : "s"} this stream scores are shown.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Your immigration profile
            </h1>
            <p className="mt-2 text-slate-600">
              Answer once — we'll calculate points and check eligibility across every supported stream.
            </p>
          </>
        )}
      </div>

      <div className="grid min-w-0 gap-5 lg:gap-8 lg:grid-cols-12">
        {/* Compact stepper (mobile) */}
        <div className="min-w-0 max-w-full overflow-hidden lg:hidden" data-testid="mobile-stepper">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>Step {step + 1} of {steps.length} · <span className="text-slate-900">{steps[Math.min(step, steps.length - 1)].label}</span></span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-[#C8102E] transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <ol className="mt-3 flex w-full max-w-full gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {steps.map((s, i) => (
              <li key={s.id} className="shrink-0">
                <button onClick={() => setStep(i)} data-testid={`mstep-${s.id}`}
                  className={`flex min-h-[40px] items-center gap-1.5 rounded-full border px-3 text-xs font-semibold ${
                    i === step ? "border-[#C8102E] bg-[#FEF2F2] text-[#C8102E]" : i < step ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500"
                  }`}>
                  {i < step ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : <span className="font-mono-tech">{i + 1}</span>} {s.label}
                </button>
              </li>
            ))}
          </ol>
        </div>

        {/* Sidebar stepper (desktop) */}
        <aside className="hidden lg:col-span-4 lg:block">
          <div className="sticky top-24 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                <span>Step {step + 1} of {steps.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-[#C8102E] transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <ol className="space-y-1" data-testid="step-tracker">
              {steps.map((s, i) => {
                const Icon = s.icon;
                const active = i === step;
                const done = i < step;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => setStep(i)}
                      data-testid={`step-${s.id}`}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors ${
                        active ? "bg-[#FEF2F2] text-[#C8102E]" : done ? "text-slate-700 hover:bg-slate-50" : "text-slate-400 hover:bg-slate-50"
                      }`}
                    >
                      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${
                        active ? "border-[#C8102E] bg-[#C8102E] text-white" : done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-slate-400"
                      }`}>
                        {done ? <Check className="h-4 w-4" strokeWidth={3} /> : <Icon className="h-4 w-4" />}
                      </span>
                      {s.label}
                    </button>
                  </li>
                );
              })}
            </ol>
            <button
              onClick={() => { resetProfile(); setStep(0); toast.info("Profile reset"); }}
              data-testid="reset-profile-btn"
              className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset profile
            </button>
          </div>
        </aside>

        {/* Form */}
        <div className="lg:col-span-8">
          <div ref={panelRef} className="scroll-mt-20 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-8" data-testid={`panel-${cur}`}>
            {cur !== "review" && (
              <StepPanel cur={cur} profile={profile} set={set} show={show} spouseOn={spouseOn} setSpouse={setSpouse}
                setLang={setLang} setFrench={setFrench} langRange={langRange} toggleConnection={toggleConnection} setConnectionType={setConnectionType} streamProvince={streamMeta?.province} />
            )}

            {cur === "review" && (
              <ReviewStep profile={profile} show={show} streamName={streamMeta?.name} streamProvince={streamMeta?.province} />
            )}

            {/* Nav */}
            <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
              <button onClick={back} disabled={step === 0} data-testid="wizard-back-btn"
                className="inline-flex min-h-[48px] items-center gap-1.5 rounded-md px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button onClick={next} disabled={loading} data-testid="wizard-next-btn"
                className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-1.5 rounded-md bg-[#C8102E] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#A60C24] disabled:opacity-60 sm:flex-none">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Assessing…</>
                  : step === steps.length - 1 ? <>{streamId ? "Calculate my score" : "Calculate results"} <ArrowRight className="h-4 w-4" /></>
                  : <>Continue <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepPanel({ cur, ...props }) {
  const Step = STEP_COMPONENTS[cur];
  return Step ? <Step {...props} /> : null;
}
